import {
  BITHOMP_API_BASE,
  BITHOMP_API_KEY,
  BITHOMP_API_KEY_HEADER,
  BITHOMP_TOKEN_PATH,
  BITHOMP_TOKEN_QUERY,
  BITHOMP_TOKENS_PATH,
  BITHOMP_TOKENS_QUERY,
  BITHOMP_TOKENS_SEARCH_PATH,
  XRPSCAN_API_BASE,
  RIPPLE_DATA_API_BASE,
} from './config'
import { XrplCurrency } from './types'

const BITHOMP_RATE_LIMIT_FALLBACK_MS = 30000
const TOKEN_LIST_TTL_MS = 5 * 60 * 1000
const TOKEN_LIST_ERROR_COOLDOWN_MS = 30000
const TOKEN_INFO_TTL_MS = 2 * 60 * 1000
let bithompRateLimitUntil = 0
let tokenListInFlight: Promise<XrplCurrency[]> | null = null
const tokenListCache: { data: XrplCurrency[] | null; fetchedAt: number; errorAt: number } = {
  data: null,
  fetchedAt: 0,
  errorAt: 0,
}
const tokenInfoCache = new Map<
  string,
  { data: XrplCurrency | null; fetchedAt: number; errorAt: number; backoffUntil: number; inFlight?: Promise<XrplCurrency | null> }
>()

const isHexCurrencyCode = (value: string) => /^[A-F0-9]{40}$/i.test(value)

const decodeHexCurrency = (value: string) => {
  if (!isHexCurrencyCode(value)) return null
  const chars = []
  for (let i = 0; i < value.length; i += 2) {
    const code = parseInt(value.slice(i, i + 2), 16)
    if (code) {
      chars.push(String.fromCharCode(code))
    }
  }
  const decoded = chars.join('').trim()
  return decoded || null
}

const parseNumber = (value: any) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const getPriceXrp = (item: any) =>
  parseNumber(
    item?.statistics?.priceXrpSpot ??
      item?.statistics?.priceXrp ??
      item?.priceXrpSpot ??
      item?.priceXrp ??
      item?.statistics?.priceNativeCurrencySpot ??
      item?.statistics?.priceNativeCurrency ??
      item?.priceNativeCurrencySpot ??
      item?.priceNativeCurrency,
  )

const getPriceXrp24h = (item: any) =>
  parseNumber(
    item?.statistics?.priceXrp24h ??
      item?.statistics?.priceNativeCurrency24h ??
      item?.priceXrp24h ??
      item?.priceNativeCurrency24h ??
      item?.metrics?.price_24h ??
      item?.metrics?.price24h,
  )

const getPriceUsd = (item: any) =>
  parseNumber(
    item?.statistics?.priceFiatsSpot?.usd ??
      item?.statistics?.priceFiats?.usd ??
      item?.priceFiatsSpot?.usd ??
      item?.priceFiats?.usd,
  )

const getVolume24h = (item: any) => {
  const totalVolume = parseNumber(item?.statistics?.totalVolume)
  if (totalVolume !== undefined) return totalVolume
  const buyVolume = parseNumber(item?.statistics?.buyVolume ?? item?.metrics?.volume_24h)
  const sellVolume = parseNumber(item?.statistics?.sellVolume)
  if (buyVolume !== undefined && sellVolume !== undefined) {
    return buyVolume + sellVolume
  }
  return buyVolume ?? sellVolume ?? parseNumber(item?.statistics?.transferVolume)
}

const now = () => Date.now()

const getRetryAfterMs = (response?: Response) => {
  const header = response?.headers?.get('retry-after')
  if (!header) return BITHOMP_RATE_LIMIT_FALLBACK_MS
  const seconds = Number(header)
  if (Number.isFinite(seconds)) {
    return seconds * 1000
  }
  const date = Date.parse(header)
  if (!Number.isNaN(date)) {
    return Math.max(date - now(), BITHOMP_RATE_LIMIT_FALLBACK_MS)
  }
  return BITHOMP_RATE_LIMIT_FALLBACK_MS
}

const setBithompRateLimit = (response?: Response) => {
  const retryAfterMs = getRetryAfterMs(response)
  bithompRateLimitUntil = Math.max(bithompRateLimitUntil, now() + retryAfterMs)
}

const isBithompRateLimited = () => now() < bithompRateLimitUntil

const RLUSD_ISSUER = 'rMxCKbEDwqr76QuheSUMdEGf4B9xJ8m5De'
let cachedXrpUsd: number | null = null
let xrpUsdPromise: Promise<number | null> | null = null

const fetchXrpUsdRate = async () => {
  if (cachedXrpUsd) {
    return cachedXrpUsd
  }
  if (xrpUsdPromise) {
    return xrpUsdPromise
  }
  xrpUsdPromise = (async () => {
    try {
      const base = RIPPLE_DATA_API_BASE.endsWith('/') ? RIPPLE_DATA_API_BASE : `${RIPPLE_DATA_API_BASE}/`
      const response = await fetch(`${base}exchange_rates/XRP/USD`)
      if (response.ok) {
        const data = await response.json()
        const rate =
          parseNumber(data?.rate) ??
          parseNumber(data?.result?.rate) ??
          parseNumber(data?.exchange_rates?.[0]?.rate)
        if (rate && rate > 0) {
          cachedXrpUsd = rate
          return rate
        }
      }
    } catch (error) {
      // ignore ripple-data failures
    }

    try {
      const base = XRPSCAN_API_BASE.endsWith('/') ? XRPSCAN_API_BASE : `${XRPSCAN_API_BASE}/`
      const response = await fetch(`${base}token/RLUSD.${RLUSD_ISSUER}`)
      if (!response.ok) {
        return null
      }
      const data = await response.json()
      const rlusdPriceXrp = parseNumber(data?.price ?? data?.metrics?.price)
      if (rlusdPriceXrp && rlusdPriceXrp > 0) {
        const rate = 1 / rlusdPriceXrp
        cachedXrpUsd = rate
        return rate
      }
    } catch (error) {
      return null
    }
    return null
  })()

  const resolved = await xrpUsdPromise
  xrpUsdPromise = null
  return resolved
}

const normalizeDecimals = (raw: any) => {
  const parsed = Number(raw ?? 6)
  if (Number.isNaN(parsed)) return 6
  return Math.min(Math.max(parsed, 0), 15)
}

const XRP_ICON_URL = '/xrp-logo.webp'

const mapToken = (item: any): XrplCurrency | null => {
  if (!item) return null
  const tokenParts = typeof item.token === 'string' ? item.token.split(':') : []
  const tokenIssuer = tokenParts.length === 2 ? tokenParts[0] : undefined
  const tokenCurrency = tokenParts.length === 2 ? tokenParts[1] : undefined

  const currencyCode =
    item.currency ||
    item.currencyCode ||
    item.currency_code ||
    item.currencyHex ||
    item.currency_hex ||
    item.currencyDetails?.currencyCode ||
    tokenCurrency ||
    item.code

  if (!currencyCode) return null

  const issuer =
    item.issuer ||
    item.issuerAddress ||
    item.issuer_address ||
    item.issuer_account ||
    item.account ||
    item.issuerDetails?.address ||
    tokenIssuer ||
    item.issuer?.address

  const decodedSymbol = decodeHexCurrency(currencyCode)
  const symbol =
    item.currencyDetails?.currency || item.symbol || item.name || decodedSymbol || currencyCode
  const name = item.name || item.description || symbol
  const decimals = normalizeDecimals(item.decimals ?? item.decimal)
  const icon = item.icon || item.logo || item.image || item.currencyDetails?.icon
  const priceXrp = getPriceXrp(item)
  const priceXrp24h = getPriceXrp24h(item)
  const priceUsd = getPriceUsd(item)
  const volume24h = getVolume24h(item)

  if (currencyCode === 'XRP') {
    return {
      currency: 'XRP',
      symbol: 'XRP',
      name: 'XRP',
      icon: icon || XRP_ICON_URL,
      decimals: 6,
      isNative: true,
      priceXrp: 1,
      priceXrp24h: 1,
      priceUsd,
      volume24h,
    }
  }

  if (!issuer) {
    return null
  }

  return {
    currency: currencyCode,
    issuer,
    symbol,
    name,
    icon,
    decimals,
    priceXrp,
    priceXrp24h,
    priceUsd,
    volume24h,
  }
}

const toUrl = (queryOverride?: string) => {
  const base = BITHOMP_API_BASE.endsWith('/') ? BITHOMP_API_BASE : `${BITHOMP_API_BASE}/`
  const path = BITHOMP_TOKENS_PATH.startsWith('/') ? BITHOMP_TOKENS_PATH.slice(1) : BITHOMP_TOKENS_PATH
  const query = queryOverride ?? BITHOMP_TOKENS_QUERY
  if (!query) {
    return `${base}${path}`
  }
  return `${base}${path}?${query}`
}

const toSearchUrl = (query: string, queryOverride?: string) => {
  const base = BITHOMP_API_BASE.endsWith('/') ? BITHOMP_API_BASE : `${BITHOMP_API_BASE}/`
  const path = BITHOMP_TOKENS_SEARCH_PATH.startsWith('/')
    ? BITHOMP_TOKENS_SEARCH_PATH.slice(1)
    : BITHOMP_TOKENS_SEARCH_PATH
  const encoded = encodeURIComponent(query)
  const searchQuery = queryOverride ? `?${queryOverride}` : ''
  return `${base}${path}/${encoded}${searchQuery}`
}

const toTokenUrl = (issuer: string, currency: string, queryOverride?: string) => {
  const base = BITHOMP_API_BASE.endsWith('/') ? BITHOMP_API_BASE : `${BITHOMP_API_BASE}/`
  const path = BITHOMP_TOKEN_PATH.startsWith('/') ? BITHOMP_TOKEN_PATH.slice(1) : BITHOMP_TOKEN_PATH
  const query = queryOverride ?? BITHOMP_TOKEN_QUERY
  const encodedIssuer = encodeURIComponent(issuer)
  const encodedCurrency = encodeURIComponent(currency)
  return query ? `${base}${path}/${encodedIssuer}/${encodedCurrency}?${query}` : `${base}${path}/${encodedIssuer}/${encodedCurrency}`
}

const toXrpscanTokenUrl = (currency: string, issuer: string) => {
  const base = XRPSCAN_API_BASE.endsWith('/') ? XRPSCAN_API_BASE : `${XRPSCAN_API_BASE}/`
  const decoded = decodeHexCurrency(currency) || currency
  const tokenCode = encodeURIComponent(decoded)
  const tokenIssuer = encodeURIComponent(issuer)
  return `${base}token/${tokenCode}.${tokenIssuer}`
}

const mapXrpscanToken = (item: any, token: XrplCurrency): XrplCurrency | null => {
  if (!item) return null
  const priceXrp = parseNumber(item?.price ?? item?.metrics?.price)
  const volume24h = parseNumber(item?.metrics?.volume_24h)
  const icon = item?.meta?.token?.icon || item?.meta?.issuer?.icon || token.icon
  const symbol = item?.code || token.symbol
  const name = item?.meta?.token?.name || token.name || symbol

  return {
    ...token,
    symbol,
    name,
    icon,
    priceXrp,
    volume24h: volume24h ?? token.volume24h,
  }
}

export const fetchBithompTokens = async (): Promise<XrplCurrency[]> => {
  const timeNow = now()
  if (tokenListCache.data && timeNow - tokenListCache.fetchedAt < TOKEN_LIST_TTL_MS) {
    return tokenListCache.data
  }
  if (tokenListCache.errorAt && timeNow - tokenListCache.errorAt < TOKEN_LIST_ERROR_COOLDOWN_MS) {
    return tokenListCache.data || []
  }
  if (isBithompRateLimited()) {
    return tokenListCache.data || []
  }
  if (tokenListInFlight) {
    return tokenListInFlight
  }

  const headers: Record<string, string> = {}
  if (BITHOMP_API_KEY) {
    headers[BITHOMP_API_KEY_HEADER] = BITHOMP_API_KEY
  }
  const fallbackQuery = 'limit=100&order=rating&currencyDetails=true'

  const fetchTokens = async (queryOverride?: string) => {
    if (isBithompRateLimited()) {
      throw new Error('Bithomp rate limited')
    }
    const response = await fetch(toUrl(queryOverride), { headers })
    if (response.status === 429) {
      setBithompRateLimit(response)
      throw new Error('Bithomp rate limited')
    }
    if (!response.ok) {
      throw new Error('Failed to fetch Bithomp token list')
    }
    return response.json()
  }

  const runFetch = async () => {
    let data
    try {
      data = await fetchTokens()
    } catch (error) {
      if (isBithompRateLimited()) {
        tokenListCache.errorAt = now()
        return tokenListCache.data || []
      }
      data = await fetchTokens(fallbackQuery)
    }

    const list = Array.isArray(data) ? data : data?.tokens || data?.data || []
    const mapped = list.map(mapToken).filter((token): token is XrplCurrency => Boolean(token))
    tokenListCache.data = mapped
    tokenListCache.fetchedAt = now()
    tokenListCache.errorAt = 0
    return mapped
  }

  tokenListInFlight = runFetch()
  try {
    return await tokenListInFlight
  } catch (error) {
    tokenListCache.errorAt = now()
    if (tokenListCache.data) {
      return tokenListCache.data
    }
    throw error
  } finally {
    tokenListInFlight = null
  }
}

export const searchBithompTokens = async (query: string): Promise<XrplCurrency[]> => {
  const trimmed = query.trim()
  if (!trimmed) return []
  if (isBithompRateLimited()) {
    return []
  }
  const headers: Record<string, string> = {}
  if (BITHOMP_API_KEY) {
    headers[BITHOMP_API_KEY_HEADER] = BITHOMP_API_KEY
  }
  const queryString = 'currencyDetails=true&lptokens=false'
  const response = await fetch(toSearchUrl(trimmed, queryString), { headers })
  if (response.status === 429) {
    setBithompRateLimit(response)
    return []
  }
  if (!response.ok) {
    throw new Error('Failed to search Bithomp tokens')
  }
  const data = await response.json()
  const list = Array.isArray(data) ? data : data?.tokens || data?.data || []
  return list.map(mapToken).filter((token): token is XrplCurrency => Boolean(token))
}

export const fetchBithompTokenInfo = async (token: XrplCurrency): Promise<XrplCurrency | null> => {
  if (!token.issuer || token.isNative || token.currency === 'XRP') {
    return token
  }
  const cacheKey = `${token.currency}:${token.issuer}`
  const cached = tokenInfoCache.get(cacheKey)
  const timeNow = now()
  if (cached?.data && timeNow - cached.fetchedAt < TOKEN_INFO_TTL_MS) {
    return cached.data
  }
  if (cached?.backoffUntil && timeNow < cached.backoffUntil) {
    return cached.data || token
  }
  if (cached?.inFlight) {
    return cached.inFlight
  }

  const runFetch = async (): Promise<XrplCurrency | null> => {
    let mapped: XrplCurrency | null = null
    const headers: Record<string, string> = {}
    if (BITHOMP_API_KEY) {
      headers[BITHOMP_API_KEY_HEADER] = BITHOMP_API_KEY
    }
    const fallbackQuery = ''
    const fetchToken = async (queryOverride?: string) => {
      if (isBithompRateLimited()) {
        throw new Error('Bithomp rate limited')
      }
      const response = await fetch(toTokenUrl(token.issuer as string, token.currency, queryOverride), { headers })
      if (response.status === 429) {
        setBithompRateLimit(response)
        throw new Error('Bithomp rate limited')
      }
      if (!response.ok) {
        throw new Error('Failed to fetch Bithomp token info')
      }
      return response.json()
    }

    if (!isBithompRateLimited()) {
      let data
      try {
        data = await fetchToken()
      } catch (error) {
        if (!isBithompRateLimited()) {
          data = await fetchToken(fallbackQuery)
        }
      }
      if (data) {
        mapped = mapToken(data)
      }
      if (mapped?.priceXrp && !mapped.priceUsd) {
        const xrpUsd = await fetchXrpUsdRate()
        if (xrpUsd) {
          mapped = {
            ...mapped,
            priceUsd: mapped.priceXrp * xrpUsd,
          }
        }
      }
      if (mapped?.priceXrp) {
        return mapped
      }
    }

    try {
      const response = await fetch(toXrpscanTokenUrl(token.currency, token.issuer), { headers })
      if (response.ok) {
        const xrpscanData = await response.json()
        let xrpscanToken = mapXrpscanToken(xrpscanData, mapped || token)
        if (xrpscanToken?.priceXrp && !xrpscanToken.priceUsd) {
          const xrpUsd = await fetchXrpUsdRate()
          if (xrpUsd) {
            xrpscanToken = {
              ...xrpscanToken,
              priceUsd: xrpscanToken.priceXrp * xrpUsd,
            }
          }
        }
        return xrpscanToken
      }
    } catch (error) {
      // ignore XRPSCAN failures
    }

    return mapped || token
  }

  const inFlight = runFetch()
  tokenInfoCache.set(cacheKey, {
    data: cached?.data || null,
    fetchedAt: cached?.fetchedAt || 0,
    errorAt: cached?.errorAt || 0,
    backoffUntil: cached?.backoffUntil || 0,
    inFlight,
  })

  try {
    const result = await inFlight
    tokenInfoCache.set(cacheKey, {
      data: result,
      fetchedAt: now(),
      errorAt: 0,
      backoffUntil: 0,
    })
    return result
  } catch (error) {
    const backoffUntil = isBithompRateLimited() ? bithompRateLimitUntil : now() + TOKEN_LIST_ERROR_COOLDOWN_MS
    tokenInfoCache.set(cacheKey, {
      data: cached?.data || null,
      fetchedAt: cached?.fetchedAt || 0,
      errorAt: now(),
      backoffUntil,
    })
    if (cached?.data) {
      return cached.data
    }
    return token
  }
}
